import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { RootState } from "features/config/store";
import { NoticeReadStatusModel } from "features/stock-ordering/core/domain/notice.model";
import {
  GetNoticeReadStatusRepository,
  GetNoticeReadStatusResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

export enum GetNoticeReadStatusState {
  initial,
  inProgress,
  success,
  fail,
}

interface InitialState {
  status: GetNoticeReadStatusState;
  message: string;
  data: NoticeReadStatusModel | undefined;
}

const initialState: InitialState = {
  status: GetNoticeReadStatusState.initial,
  message: "",
  data: undefined,
};

export const getNoticeReadStatus = createAsyncThunk(
  "getNoticeReadStatus",
  async (noticeId: number, { rejectWithValue }) => {
    try {
      const response: GetNoticeReadStatusResponse =
        await GetNoticeReadStatusRepository(noticeId);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (!error.response) {
          throw error;
        }
        throw rejectWithValue(error.response.data.message);
      }
    }
  }
);

export const getNoticeReadStatusSlice = createSlice({
  name: "getNoticeReadStatus",
  initialState,
  reducers: {
    resetGetNoticeReadStatus: (state) => {
      state.status = GetNoticeReadStatusState.initial;
      state.data = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNoticeReadStatus.pending, (state) => {
        state.status = GetNoticeReadStatusState.inProgress;
      })
      .addCase(getNoticeReadStatus.fulfilled, (state, action) => {
        if (action.payload) {
          const { message, data } = action.payload;
          state.status = GetNoticeReadStatusState.success;
          state.message = message;
          state.data = data;
        }
      })
      .addCase(getNoticeReadStatus.rejected, (state, action) => {
        state.status = GetNoticeReadStatusState.fail;
        state.message = action.payload as string;
        state.data = undefined;
      });
  },
});

export const selectGetNoticeReadStatus = (state: RootState) =>
  state.getNoticeReadStatus;
export const { resetGetNoticeReadStatus } = getNoticeReadStatusSlice.actions;
export default getNoticeReadStatusSlice.reducer;
